// CabeceraPaginaSection.js — Sección `cabecera_pagina`: banda de título de una
// página interna (barra degradada encima, título, subtítulo y fondo con imagen).
//
// En la vista de un artículo (/hub/:articulo) puede ocultar el subtítulo y usar
// otro espaciado superior (`ocultar_subtitulo_articulo`, `padding_articulo`),
// que es como la hace el hub de LAAC Academy.
import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useParams } from 'react-router-dom';
import { BACKEND_BASE } from '../../../services/api';

const urlImagen = (v) => (!v ? null : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));

// Entrada deslizándose desde la izquierda, como el resto de LAAC.
const entrar = keyframes`from { opacity: 0; transform: translate3d(-40px, 0, 0); } to { opacity: 1; transform: none; }`;
const animacion = css`
  animation: ${entrar} 700ms ease both;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Wrap = styled.section`
  position: relative;
  overflow: hidden;
  background-color: ${p => p.$color};
  ${p => p.$img && `
    background-image: ${p.$overlay ? `linear-gradient(${p.$overlay}, ${p.$overlay}), ` : ''}url(${p.$img});
    background-size: cover;
    background-position: ${p.$pos || 'center'};
    background-attachment: ${p.$parallax ? 'fixed' : 'scroll'};
  `}
  padding: ${p => p.$padding};
  @media (max-width: 980px) {
    background-attachment: scroll;
    padding: ${p => p.$paddingMovil};
  }
`;

const Fila = styled.div`
  ${p => p.$animar && animacion}
  width: 90%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  text-align: ${p => p.$align};
  @media (max-width: 980px) { text-align: ${p => p.$alignMovil}; }
`;

const Titulo = styled.h1`
  position: relative;
  display: block;
  margin: 0 0 20px;
  ${p => p.$ancho && `max-width: ${p.$ancho};`}
  ${p => p.$ancho && p.$align === 'center' && 'margin-left: auto; margin-right: auto;'}
  padding: 0;
  font-family: ${p => p.$fuente};
  font-size: ${p => p.$size};
  font-weight: ${p => p.$peso};
  line-height: 1.1em;
  color: ${p => p.$color};
  ${p => p.$barra && `
    &::before {
      content: '';
      position: absolute;
      top: -20px;
      left: ${p.$align === 'center' ? '50%' : '0'};
      ${p.$align === 'center' ? 'transform: translateX(-50%);' : ''}
      width: ${p.$barraAncho};
      height: ${p.$barraAlto};
      border-radius: 3px;
      background: ${p.$barra};
    }
  `}
  @media (max-width: 980px) {
    font-size: ${p => p.$sizeMovil};
    ${p => p.$ancho && 'margin-left: auto; margin-right: auto;'}
    ${p => p.$barra && `&::before { left: 50%; transform: translateX(-50%); }`}
  }
`;

const Subtitulo = styled.p`
  margin: 0;
  max-width: ${p => p.$ancho};
  ${p => p.$align === 'center' && 'margin-left: auto; margin-right: auto;'}
  font-family: ${p => p.$fuente};
  font-size: ${p => p.$size};
  line-height: 1.35em;
  color: ${p => p.$color};
  white-space: pre-line;
  /* Los saltos de línea del admin son para el escritorio; en móvil el texto fluye. */
  @media (max-width: 980px) { font-size: ${p => p.$sizeMovil}; margin-left: auto; margin-right: auto; white-space: normal; }
`;

const CabeceraPaginaSection = ({ config = {}, tl = (v, d = '') => v || d }) => {
  const { articulo } = useParams();
  const c = config;
  const enArticulo = !!articulo;
  const titulo = tl(c.titulo, '');
  const subtitulo = enArticulo && c.ocultar_subtitulo_articulo !== false ? '' : tl(c.subtitulo, '');
  const align = c.alineacion || 'left';

  return (
    <Wrap
      $color={c.color_fondo || '#f3eefc'}
      $img={urlImagen(c.imagen_fondo)}
      $overlay={c.overlay_fondo}
      $pos={c.posicion_fondo}
      $parallax={c.parallax !== false}
      $padding={(enArticulo && c.padding_articulo) || c.padding || '86px 0 56px'}
      $paddingMovil={c.padding_movil || '60px 0 40px'}
    >
      <Fila $animar={c.animar !== false} $ancho={c.ancho_maximo || '1200px'} $align={align} $alignMovil={c.alineacion_movil || 'center'}>
        {titulo && (
          <Titulo
            $fuente={c.fuente_titulo || "'Roboto', Helvetica, Arial, sans-serif"}
            $size={c.tamano_titulo || '50px'}
            $sizeMovil={c.tamano_titulo_movil || '30px'}
            $peso={c.peso_titulo || '700'}
            $color={c.color_titulo || '#012947'}
            $ancho={c.ancho_titulo}
            $align={align}
            $barra={c.mostrar_barra === false ? null : (c.barra_degradado || 'linear-gradient(90deg, #d244eb 0%, #a855f7 25%, #3b82f6 55%, #2d91cc 75%, #05c2d3 100%)')}
            $barraAncho={c.barra_ancho || '127px'}
            $barraAlto={c.barra_alto || '7px'}
          >
            {titulo}
          </Titulo>
        )}
        {subtitulo && (
          <Subtitulo
            $fuente={c.fuente_subtitulo || 'Arial, Helvetica, sans-serif'}
            $size={c.tamano_subtitulo || '20px'}
            $sizeMovil={c.tamano_subtitulo_movil || '17px'}
            $color={c.color_subtitulo || '#4d4d4d'}
            $ancho={c.ancho_subtitulo || '560px'}
            $align={align}
          >
            {subtitulo}
          </Subtitulo>
        )}
      </Fila>
    </Wrap>
  );
};

export default CabeceraPaginaSection;
